import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    FaHistory, 
    FaSpinner, 
    FaExclamationTriangle, 
    FaSearch,
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './ManagerActivities.css';

const ITEMS_PER_PAGE = 100;

const ManagerActivities = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/reports/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const activitiesData = Array.isArray(response.data) ? response.data : 
                                 response.data.activities ? response.data.activities : [];
            console.log(activitiesData);
            setActivities(activitiesData);
            setError(null);
        } catch (error) {
            console.error('Error fetching activities:', error);
            setError('Failed to fetch activities. Please try again later.');
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }).format(date);
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const filteredActivities = activities.filter(activity => {
        if (!activity) return false;
        return (activity.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentActivities = filteredActivities.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return (
            <div className="pagination">
                <button 
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <FaChevronLeft />
                </button>

                {startPage > 1 && (
                    <>
                        <button 
                            className="pagination-button"
                            onClick={() => handlePageChange(1)}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                    </>
                )}

                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(number => (
                    <button
                        key={number}
                        className={`pagination-button ${number === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(number)}
                    >
                        {number}
                    </button>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                        <button 
                            className="pagination-button"
                            onClick={() => handlePageChange(totalPages)}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <button 
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <FaChevronRight />
                </button>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Topbar />
                <Sidebar />
                <div className="loading-content">
                    <FaSpinner className="spinner" />
                    <p>Loading activities...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <Topbar />
                <Sidebar />
                <div className="error-content">
                    <FaExclamationTriangle className="error-icon" />
                    <p>{error}</p>
                    <button onClick={fetchActivities} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="activities-container">
                    <div className="activities-header">
                    <div className="activities-header-left">
                        <div className="title-section">
                            <h1>
                                <FaHistory className="title-icon" />
                                System Activities
                            </h1>
                            <p className="subtitle">Track all activities in the system</p>
                        </div>
                    </div>

                    <div className="search-section">
                        <div className="search-box">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search activities..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>
                    </div>

                    <div className="activities-timeline">
                        {currentActivities.length === 0 ? (
                            <div className="no-activities">
                                <FaHistory className="no-activities-icon" />
                                <p>No activities found</p>
                            </div>
                        ) : (
                            <>
                                {currentActivities.map((activity, index) => (
                                    <div key={index} className="activity-card">
                                        <div className="activity-content">
                                            <p className="activity-description">
                                                {activity.description || 'No description available'}
                                            </p>
                                            <div className="activity-time">
                                                <FaCalendarAlt className="calendar-icon" />
                                                <span>{formatDate(activity.created_at)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagerActivities;
